import {
  appError,
  clearErrorHistory,
  dismissError,
  errorHistory,
  parseStackTrace,
  showingHistory,
  toggleHistory,
} from '../../state/error';
import { t } from '../../state/i18n';

// ============================================================================
// ErrorOverlay - Global error display with stack trace
// ============================================================================

const WarningIcon = () => (
  <div class="w-14 h-14 rounded-full bg-red-500/20 border-2 border-red-500/40 flex items-center justify-center">
    <svg
      class="w-7 h-7 text-red-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  </div>
);

const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
    aria-label="Dismiss error"
  >
    <svg
      class="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
);

const StackTraceView = ({ stack }: { stack: string }) => {
  const frames = parseStackTrace({ stack });

  return (
    <div class="bg-slate-900/80 rounded-lg border border-slate-700/50 overflow-hidden">
      <div class="px-3 py-2 border-b border-slate-700/50 bg-slate-800/30">
        <span class="text-xs font-mono text-slate-400">{t('ui.errors.stackTrace')}</span>
      </div>
      <div class="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
        <div class="p-3 space-y-1">
          {frames.map((frame, idx) => (
            <div key={idx} class="font-mono text-xs leading-relaxed">
              <span class="text-slate-500">at </span>
              <span class="text-amber-400">{frame.functionName}</span>
              <span class="text-slate-500"> (</span>
              <span class="text-cyan-400">{frame.fileName}</span>
              {frame.line !== undefined && (
                <>
                  <span class="text-slate-500">:</span>
                  <span class="text-green-400">{frame.line}</span>
                </>
              )}
              {frame.column !== undefined && (
                <>
                  <span class="text-slate-500">:</span>
                  <span class="text-green-400/70">{frame.column}</span>
                </>
              )}
              <span class="text-slate-500">)</span>
            </div>
          ))}
          {frames.length === 0 && (
            <div class="text-slate-500 text-xs italic">{t('ui.empty.noStackTrace')}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const ErrorHistoryView = () => {
  const history = errorHistory.value;

  if (history.length === 0) {
    return (
      <div class="text-center py-8 text-slate-500 text-sm">{t('ui.empty.noErrorsInHistory')}</div>
    );
  }

  return (
    <div class="space-y-3 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
      {history.map((error, idx) => (
        <div key={idx} class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
          <div class="flex items-start justify-between gap-2">
            <p class="text-sm text-red-300 font-medium break-all">{error.message}</p>
            <span class="text-xs text-slate-500 whitespace-nowrap">
              {new Date(error.timestamp).toLocaleTimeString()}
            </span>
          </div>
          {error.source && (
            <p class="text-xs text-slate-500 mt-1 font-mono">
              {error.source}
              {error.line !== undefined && `:${error.line}`}
              {error.column !== undefined && `:${error.column}`}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const ActionButton = ({
  onClick,
  variant = 'secondary',
  children,
}: {
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  children: preact.ComponentChildren;
}) => {
  const baseClasses =
    'px-4 py-2 rounded-lg font-mono text-sm font-medium transition-all duration-200 flex items-center gap-2';
  const variantClasses = {
    primary: 'bg-indigo-600 hover:bg-indigo-500 text-white',
    secondary: 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 border border-slate-600/50',
    danger: 'bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30',
  };

  return (
    <button type="button" onClick={onClick} class={`${baseClasses} ${variantClasses[variant]}`}>
      {children}
    </button>
  );
};

const copyToClipboard = ({ text }: { text: string }): void => {
  navigator.clipboard.writeText(text).catch(() => {
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  });
};

export const ErrorOverlay = () => {
  const error = appError.value;
  const isShowingHistory = showingHistory.value;
  const historyCount = errorHistory.value.length;

  if (!error) {
    return null;
  }

  const handleCopyStack = () => {
    const fullError = `${error.message}\n\n${error.stack ?? 'No stack trace'}`;
    copyToClipboard({ text: fullError });
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      dismissError();
    }
  };

  // Add keyboard listener when mounted
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', handleKeyDown);
  }

  return (
    <div class="fixed inset-0 flex items-center justify-center z-[9999]">
      {/* Backdrop */}
      <button
        type="button"
        class="absolute inset-0 bg-slate-950/90 backdrop-blur-sm border-none cursor-default"
        onClick={dismissError}
        aria-label="Dismiss error"
      />

      {/* Error Card */}
      <div class="relative w-full max-w-2xl mx-4 animate-fade-in">
        <div class="bg-gradient-to-b from-slate-900 to-slate-950 rounded-2xl border border-red-500/20 shadow-2xl shadow-red-500/10 overflow-hidden">
          {/* Header */}
          <div class="relative px-6 py-5 border-b border-slate-800/50 bg-red-500/5">
            <CloseButton onClick={dismissError} />
            <div class="flex items-center gap-4">
              <WarningIcon />
              <div>
                <h2 class="text-lg font-semibold text-white">{t('ui.errors.runtimeError')}</h2>
                <p class="text-sm text-slate-400 mt-0.5">{t('ui.errors.errorOccurred')}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div class="p-6 space-y-4">
            {/* Error Message */}
            <div class="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p class="text-red-300 font-mono text-sm break-all leading-relaxed">
                {error.message}
              </p>
              {error.source && (
                <p class="text-red-400/60 font-mono text-xs mt-2">
                  {error.source}
                  {error.line !== undefined && `:${error.line}`}
                  {error.column !== undefined && `:${error.column}`}
                </p>
              )}
            </div>

            {/* Stack Trace or History */}
            {isShowingHistory ? (
              <div>
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm font-medium text-slate-300">
                    {t('ui.errors.errorHistory', { count: historyCount })}
                  </span>
                  <button
                    type="button"
                    onClick={clearErrorHistory}
                    class="text-xs text-slate-500 hover:text-slate-400 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
                <ErrorHistoryView />
              </div>
            ) : (
              error.stack && <StackTraceView stack={error.stack} />
            )}
          </div>

          {/* Actions */}
          <div class="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex flex-wrap items-center gap-3">
            <ActionButton onClick={handleCopyStack} variant="secondary">
              <svg
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              {t('ui.buttons.copy')}
            </ActionButton>

            <ActionButton onClick={toggleHistory} variant="secondary">
              <svg
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {isShowingHistory
                ? t('ui.errors.stackTrace')
                : t('ui.errors.errorHistory', { count: historyCount })}
            </ActionButton>

            <div class="flex-1" />

            <ActionButton onClick={dismissError} variant="secondary">
              {t('ui.buttons.continue')}
            </ActionButton>

            <ActionButton onClick={handleReload} variant="primary">
              <svg
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t('ui.buttons.reload')}
            </ActionButton>
          </div>
        </div>
      </div>
    </div>
  );
};
